import React, { useEffect, useState } from "react";
import { Header as TableHeadings, Rows as TableRows } from "../table";
import Checkbox from "src/scenes/EmployeeList/components/Checkbox";
import { ActivateFeatureButton, DataTable, DataTableContainer, FlexContainer, PortalSearchInput, PortalSelect, PortalTableActions, PortalTableControls, UpperControls } from "src/components";
import { DeleteButtons } from "../style";
import { useTableHooks } from "../../hooks/useTableHooks";
import {
    DIRECTORY_LEVEL,
    getChildDirectories,
    getDirectoryPath,
    getParentSubcategoryUuid
} from "../../directoryLevels";

export const ContentTable = (props) => {
    const { data, manageContent, subcategoriesList, companyCategoriesList } = props;
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedCategory, setSelectedCategory] = useState("category");
    const [selectedSubcategory, setSelectedSubcategory] = useState("subcategory");
    const [selectedTopic, setSelectedTopic] = useState("topic");
    const [selectedSubtopic, setSelectedSubtopic] = useState("subtopic");
    const SortableColumns = [
        "select", // Not an actual column, no effect
        "image_video", // Not an actual column, no effect
        "title",
        "description",
        item => getContentLocation(item),
        "visibility",
        "view_edit" // Not an actual column, no effect
    ];
    const {
        tableData,
        selectedItem,
        selectAllItem,
        handleMainCheckbox,
        toggleItemSelection,
        isItemSelected,
        handleSortColumn,
        handleDeleteSelectedItems,
        setTableData,
    } = useTableHooks(data, SortableColumns);

    const getContentDirectory = item =>
        subcategoriesList.find(directory => directory.uuid === (item.subcategoryUuid || item.subcategory?.uuid));

    function getContentLocation(item) {
        const directory = getContentDirectory(item);
        return directory ?
            getDirectoryPath(directory, subcategoriesList, companyCategoriesList) :
            [item?.category?.name, item?.categoryName].filter(Boolean).join(" > ");
    }

    const headings = [
        {
            name: <Checkbox
                checked={selectAllItem}
                onChange={() => handleMainCheckbox()}
                onClick={e => e.stopPropagation()}
            />, textAlign: "center"
        },
        { name: "Image/Video", textAlign: "center" },
        { name: "Title", textAlign: "left" },
        { name: "Description", textAlign: "left" },
        { name: "Content Location", textAlign: "left" },
        { name: "Visibility", textAlign: "left" },
        { name: "View/Edit", textAlign: "left" },
    ];

    const isASortableColumn = (index) => index > 0;

    const subcategoryOptions = selectedCategory === "category" ? [] :
        getChildDirectories(subcategoriesList, selectedCategory, "", DIRECTORY_LEVEL.SUBCATEGORY);
    const topicOptions = selectedSubcategory === "subcategory" ? [] :
        getChildDirectories(subcategoriesList, selectedCategory, selectedSubcategory, DIRECTORY_LEVEL.TOPIC);
    const subtopicOptions = selectedTopic === "topic" ? [] :
        getChildDirectories(subcategoriesList, selectedCategory, selectedTopic, DIRECTORY_LEVEL.SUBTOPIC);

    const hasAncestor = (directory, ancestorUuid) => {
        let parentUuid = getParentSubcategoryUuid(directory);
        while (parentUuid) {
            if (parentUuid === ancestorUuid) return true;
            const parent = subcategoriesList.find(item => item.uuid === parentUuid);
            parentUuid = getParentSubcategoryUuid(parent);
        }
        return false;
    };

    useEffect(() => {
        const filteredData = data.filter(item => {
            const directory = getContentDirectory(item);
            const matchesSearch = !searchTerm.trim() ||
                item?.title?.toLowerCase().includes(searchTerm.trim().toLowerCase());
            const matchesCategory = selectedCategory === "category" ||
                item?.category?.uuid === selectedCategory ||
                directory?.categoryUuid === selectedCategory;
            const matchesSubcategory = selectedSubcategory === "subcategory" ||
                directory?.uuid === selectedSubcategory ||
                hasAncestor(directory, selectedSubcategory);
            const matchesTopic = selectedTopic === "topic" ||
                directory?.uuid === selectedTopic ||
                hasAncestor(directory, selectedTopic);
            const matchesSubtopic = selectedSubtopic === "subtopic" ||
                directory?.uuid === selectedSubtopic;

            return matchesSearch && matchesCategory && matchesSubcategory && matchesTopic && matchesSubtopic;
        });

        setTableData(filteredData);
    }, [data, searchTerm, selectedCategory, selectedSubcategory, selectedTopic, selectedSubtopic, subcategoriesList]);

    const onCategoryChange = event => {
        setSelectedCategory(event.target.value);
        setSelectedSubcategory("subcategory");
        setSelectedTopic("topic");
        setSelectedSubtopic("subtopic");
    };

    const onSubcategoryChange = event => {
        setSelectedSubcategory(event.target.value);
        setSelectedTopic("topic");
        setSelectedSubtopic("subtopic");
    };

    const onTopicChange = event => {
        setSelectedTopic(event.target.value);
        setSelectedSubtopic("subtopic");
    };

    return (
        <FlexContainer>
            <UpperControls>
                <PortalTableControls>
                    <PortalSearchInput
                        placeholder="Search Content"
                        type="text"
                        value={searchTerm}
                        onChange={event => setSearchTerm(event.target.value)}
                    />
                    <PortalSelect value={selectedCategory} onChange={onCategoryChange} width="180px">
                        <option value={"category"}>All Categories</option>
                        {companyCategoriesList?.length > 0 && companyCategoriesList?.map(sub => (
                            <option key={sub.uuid} value={sub.uuid}>
                                {sub.name}
                            </option>
                        ))}
                    </PortalSelect>

                    <PortalSelect value={selectedSubcategory} onChange={onSubcategoryChange} width="180px" disabled={selectedCategory === "category"}>
                        <option value={"subcategory"}>All Sub-Categories</option>
                        {subcategoryOptions.map(sub => (
                            <option key={sub.uuid} value={sub.uuid}>
                                {sub.categoryName}
                            </option>
                        ))}
                    </PortalSelect>

                    <PortalSelect value={selectedTopic} onChange={onTopicChange} width="180px" disabled={selectedSubcategory === "subcategory"}>
                        <option value={"topic"}>All Topics</option>
                        {topicOptions.map(topic => (
                            <option key={topic.uuid} value={topic.uuid}>
                                {topic.categoryName}
                            </option>
                        ))}
                    </PortalSelect>

                    <PortalSelect value={selectedSubtopic} onChange={event => setSelectedSubtopic(event.target.value)} width="180px" disabled={selectedTopic === "topic"}>
                        <option value={"subtopic"}>All Subtopics</option>
                        {subtopicOptions.map(subtopic => (
                            <option key={subtopic.uuid} value={subtopic.uuid}>
                                {subtopic.categoryName}
                            </option>
                        ))}
                    </PortalSelect>

                    {(selectedItem.length > 0) && (
                        <DeleteButtons
                            onClick={() => handleDeleteSelectedItems()}>
                            Delete Content
                        </DeleteButtons>
                    )}
                </PortalTableControls>
                <PortalTableActions>
                    <ActivateFeatureButton
                        width="184px"
                        height="39px"
                        onClick={() => manageContent.toggleAddContentModal()}
                    >
                        Add Content
                    </ActivateFeatureButton>
                </PortalTableActions>
            </UpperControls>
            <DataTableContainer>
                <DataTable minWidth="1040px">
                    <thead>
                        <TableHeadings
                            headings={headings}
                            onSort={handleSortColumn}
                            onMainCheck={handleMainCheckbox}
                            isSortable={isASortableColumn}
                        />
                    </thead>
                    <tbody>
                        <TableRows
                            tableData={tableData}
                            onToggle={(quote) => toggleItemSelection(quote)}
                            isSelected={isItemSelected}
                            manageContent={manageContent}
                            getContentLocation={getContentLocation}
                        />
                    </tbody>
                </DataTable>
            </DataTableContainer>
        </FlexContainer>
    );
};
